/// <amd-module name="Checks/ChecksOverview/ChecksDetailsTable" />
import React = require("react");

import { ChecksPanel } from "Checks/ChecksOverview/ChecksPanel";
import * as Resources from "Checks/ChecksOverview/Resources";
import { CheckAction, ChecksTableCellRenderer } from "Checks/Client/Contracts/Contracts";
import * as PipelineChecksContracts from "Checks/Client/Contracts/PipelinesChecks";

import { ObservableValue } from "VSS/Core/Observable";
import { ScreenBreakpoints } from "VSS/Core/Util/Screen";
import { equals, format } from "VSS/Core/Util/String";
import { IVssComponentContext, IVssComponentProperties, VssComponent, WrappedComponent } from "VSS/Platform/Layout";

import { FeatureFlagConstants, TaskCheckConstants } from "Checks/ChecksOverview/Generated/Constants";
import { CheckEvaluationOrder } from "Checks/Client/Contracts/PipelinesChecks";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { Button } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { Dialog } from "VSSUI/Dialog";
import { Header } from "VSSUI/Header";
import { Link } from "VSSUI/Link";
import { IMenuItem, IMenuProps } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { Pill, PillSize, PillVariant } from "VSSUI/Pill";
import { ColumnMore, IMeasurementStyle, ITableBreakpoint, ITableColumn, ITableRow, ITableRowDetails, SimpleTableCell, Table, TableCell, TableRow, renderColumns } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { css } from "VSSUI/Util";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

export interface ICheckDetailsTableProps extends IVssComponentProperties {
    checkConfigurationData: PipelineChecksContracts.CheckConfigurationData[];
    checkDefinitions: PipelineChecksContracts.CheckDefinitionData[];
    onDeleteCheck: (id: number, checkType: string) => void;
    onUpdateCheck: (check: PipelineChecksContracts.CheckConfiguration, definitionRefId: string, checkType: string, showError: boolean) => Promise<void>;
    hasManagePermission: boolean;
    defaultCheckSettings?: { [key: string] : string; };
}

export interface ICheckDetailsRow {
    checkOrder?: number;
    checkComponentContributionId: string;
    checkComponentContributionDependencies: string[];
    checkConfigId: number;
    checkDefinitionRefId: string;
    checkTypeDisplayName: string;
    checkTimeout: number;
    checkConfig: PipelineChecksContracts.CheckConfiguration;
    checkDefinition: Object;
}

export class ChecksDetailsTable extends VssComponent<ICheckDetailsTableProps, {}> {

    constructor(props: ICheckDetailsTableProps, context: IVssComponentContext) {
        super(props, context);
        this._showDeleteCheckDialog = new ObservableValue<boolean>(false);
        this._showCheckConfigPanel = new ObservableValue<boolean>(false);

        this._isDisableChecksFeatureEnabled = isFeatureFlagEnabled(context.pageContext, "Pipelines.Checks.EnableDisabledChecksFeature", false);
        this._isEnableOrderingChecksEnabled = isFeatureFlagEnabled(context.pageContext, "Pipelines.Checks.EnableOrderingChecks", false);
        this._initTableColumns();

        //Taking the resource of the first check as all the checks will be on a same resource
        this._checkResource = this.props.checkConfigurationData[0].checkConfiguration.resource;

        let enableChecksScalabilityPhase1WarningsFF = isFeatureFlagEnabled(
            context.pageContext,
            FeatureFlagConstants.EnableChecksScalabilityPhase1Warnings,
            false
        );
        let enableChecksScalabilityPhase2ErrorsFF = isFeatureFlagEnabled(
            context.pageContext,
            FeatureFlagConstants.EnableChecksScalabilityPhase2Errors,
            false
        );
        let enableChecksScalabilityPhase3FailChecksFF = isFeatureFlagEnabled(
            context.pageContext,
            FeatureFlagConstants.EnableChecksScalabilityPhase3FailCheckRuns,
            false
        );

        this._isAllowChangeOrderEnabled = this._isEnableOrderingChecksEnabled && isFeatureFlagEnabled(
            context.pageContext,
            FeatureFlagConstants.AllowChangingApprovalOrder,
            false
        );

        this._showCheckErrors = enableChecksScalabilityPhase2ErrorsFF || enableChecksScalabilityPhase3FailChecksFF;
        this._showCheckIssues = enableChecksScalabilityPhase1WarningsFF || this._showCheckErrors;
    }

    public render(): JSX.Element {
        const checksList = this.props.checkConfigurationData;
        if (!checksList) {
            return <div />;
        }

        let checkDetails: ArrayItemProvider<ICheckDetailsRow>;
        let renderHeader = undefined;
        let renderRow = undefined;

        if (this._isEnableOrderingChecksEnabled) {
            checkDetails = this._getEvaluationOrderedCheckDetails(checksList);
            renderHeader = this._getCardHeader;
            renderRow = (index: number, item: ICheckDetailsRow, details: ITableRowDetails<ICheckDetailsRow>) => this._renderRow(index, item, details, checkDetails);
        } else {
            checkDetails = this._getCheckDetails(checksList);
        }

        return (
            <Card className="flex-grow bolt-table-card" contentProps={{ contentPadding: false }} renderHeader={renderHeader}>
                <Table
                    key={"checks-details-table"}
                    ariaLabel={Resources.ChecksTable}
                    columns={this._tableColumns}
                    itemProvider={checkDetails}
                    onActivate={this._onRowClicked}
                    tableBreakpoints={this._tableBreakpoints}
                    role="table"
                    renderRow={renderRow} />
                <Observer showCheckConfigPanel={this._showCheckConfigPanel}>
                    {(observerProps: { showCheckConfigPanel: boolean }) => {
                        return (
                            observerProps.showCheckConfigPanel &&
                            <ChecksPanel
                                contributionType={this._selectedCheckRowDetails.checkComponentContributionId}
                                onUpdateCheck={this._onUpdateCheck}
                                onClosePanel={this.onClosePanel}
                                resource={this._checkResource}
                                checkConfigObject={this._selectedCheckRowDetails.checkConfig}
                                checkDefinition={this._selectedCheckRowDetails.checkDefinition}
                                timeout={this._selectedCheckRowDetails.checkConfig.timeout}
                                checkAction={CheckAction.Edit}
                                checkName={this._selectedCheckRowDetails.checkTypeDisplayName}
                                checkDefinitionRefId={this._selectedCheckRowDetails.checkDefinitionRefId}
                                componentDependencies={this._selectedCheckRowDetails.checkComponentContributionDependencies}
                                isDisableChecksFeatureEnabled={this._isDisableChecksFeatureEnabled}
                                defaultCheckSettings={this.props.defaultCheckSettings} />
                        );
                    }}
                </Observer>
                <Observer showDeleteCheckDialog={this._showDeleteCheckDialog} >
                    {(observerProps: { showDeleteCheckDialog: boolean }) => {
                        return (
                            observerProps.showDeleteCheckDialog && <Dialog
                                calloutContentClassName="delete-checks-dialog"
                                titleProps={{ text: Resources.DeleteDialogHeader }}
                                footerButtonProps={[
                                    {
                                        text: Resources.Cancel,
                                        onClick: this._onCancelDelete,
                                        autoFocus: true
                                    },
                                    {
                                        text: Resources.DeleteIconName,
                                        primary: true,
                                        onClick: this._onDeleteConfirm
                                    }
                                ]}
                                onDismiss={this._onCancelDelete}>
                                {Resources.DeleteCheckConfirmationText}
                            </Dialog>
                        );
                    }}
                </Observer>
            </Card>
        );
    }

    private _getCardHeader = (): JSX.Element => {
        return (
            <div className="flex-row justify-space-between">
                <div>
                    <Header
                        className="bolt-card-header"
                        title={"Check execution order"}
                    />
                    <div className="bolt-default-horizontal-spacing flex-row flex-noshrink flex-start">
                        <span className="secondary-text flex-grow flex-self-stretch">
                            {Resources.CheckDetailsTableSecondaryText}{" "}
                            <Link
                                href={Resources.CheckEvaluationOrderLearnMoreLink}
                                target="_blank"
                            >
                                {Resources.CheckDetailsTableLearnMoreText}
                            </Link>
                        </span>
                    </div>
                </div>
                {this.props.showAddCheckButton && (
                    <div className="margin-horizontal-20 margin-top-16">
                        {this._getAddChecksButton()}
                    </div>
                )}
            </div>
        );
    }

    private _renderRow = (index: number, item: ICheckDetailsRow, details: ITableRowDetails<ICheckDetailsRow>, itemProvider: ArrayItemProvider<ICheckDetailsRow>) => {
        const rowDetails: ITableRowDetails<ICheckDetailsRow> = {
            selectableText: details.selectableText,
            ariaRowOffset: details.ariaRowOffset,
            data: details.data,
            excludeTabStop: details.excludeTabStop,
            itemProvider: itemProvider,
            listProps: details.listProps,
            onFocusItem: details.onFocusItem,
            renderSpacer: undefined,
            selection: undefined,
            singleClickActivation: false,
            role: undefined,
            eventDispatch: details.eventDispatch,
        };

        const isFirstCheckOfEvaluationOrder =
            item.checkOrder && item.checkOrder !== 1; // skip adding spacing to the first row

        return (
            <React.Fragment>
                {isFirstCheckOfEvaluationOrder ? (
                    <tr
                        className="check-details-table-first-evaluation-spacer-row"
                        role="presentation"
                        aria-hidden
                    >
                        <td colSpan={100} />
                    </tr>
                ) : (
                    <tr
                        className="bolt-list-row-spacer invisible"
                        role="presentation"
                        aria-hidden
                    />
                )}
                <TableRow
                    details={rowDetails}
                    index={index}
                    linkProps={(item as any).linkProps}
                >
                    {renderColumns(index, this._tableColumns, item, rowDetails)}
                </TableRow>
            </React.Fragment>
        );
    }

    private _getAddChecksButton = (): JSX.Element => {
        return (
            <div className="checks-create-button-container">
                <Button
                    className="checks-create-button-with-text"
                    disabled={!this.props.hasManagePermission}
                    text="Add new"
                    ariaLabel={Resources.AddCheck}
                    iconProps={{
                        iconName: "Add",
                        tooltipProps: { text: Resources.AddCheck },
                    }}
                    onClick={() => this.props.onAddCheckButtonClicked()}
                />
            </div>
        );
    }

    private _getEvaluationOrderedCheckDetails = (checksList: PipelineChecksContracts.CheckConfigurationData[]): ArrayItemProvider<ICheckDetailsRow> => {
        let evaluationOrderToCheckRows = new Map<
            CheckEvaluationOrder,
            ICheckDetailsRow[]
        >();
        let evaluationOrders = new Set<CheckEvaluationOrder>();

        checksList.map((check) => {
            let checkDefinitionData = this._getCheckDefinitionDetails(
                check.definitionRefId
            );
            if (checkDefinitionData != null) {
                const row: ICheckDetailsRow = {
                    checkComponentContributionId: checkDefinitionData.uiContributionType,
                    checkComponentContributionDependencies:
                        checkDefinitionData.uiContributionDependencies,
                    checkConfigId: check.checkConfiguration.id,
                    checkDefinitionRefId: check.definitionRefId,
                    checkTypeDisplayName: checkDefinitionData.name,
                    checkTimeout: check.checkConfiguration.timeout,
                    checkConfig: check.checkConfiguration,
                    checkDefinition: checkDefinitionData.checkDefinition,
                };

                const evaluationOrder = checkDefinitionData.checkEvaluationOrder;
                evaluationOrders.add(evaluationOrder); // track the evaluationOrders to sort the rows based on their order

                // populate the map { evaluationOrder: [checkDetail1, checkDetail2...] }
                let checkDetails: ICheckDetailsRow[];
                if (evaluationOrderToCheckRows.has(evaluationOrder)) {
                    checkDetails = evaluationOrderToCheckRows.get(
                        evaluationOrder
                    ) as ICheckDetailsRow[];
                    checkDetails.push(row);
                } else {
                    checkDetails = [row];
                }
                evaluationOrderToCheckRows.set(evaluationOrder, checkDetails);
            }
        });

        let tableRows: ICheckDetailsRow[] = [];

        const sortedEvOrders = Array.from(evaluationOrders).sort();
        let orderNumber = 1;
        sortedEvOrders.map((evOrder) => {
            let checkDetails = evaluationOrderToCheckRows.get(
                evOrder
            ) as ICheckDetailsRow[];
            checkDetails[0].checkOrder = orderNumber; // only set the order number to the first row of each evaluation order step
            tableRows.push(...checkDetails);
            orderNumber++;
        });

        return new ArrayItemProvider<ICheckDetailsRow>(tableRows);
    }

    private _getCheckDetails = (checksList: PipelineChecksContracts.CheckConfigurationData[]): ArrayItemProvider<ICheckDetailsRow> => {
        let tableRows: ICheckDetailsRow[] = [];
        checksList.map((check) => {
            let checkDefinitionData = this._getCheckDefinitionDetails(check.definitionRefId);
            if (checkDefinitionData != null) {
                tableRows.push({
                    checkComponentContributionId: checkDefinitionData.uiContributionType,
                    checkComponentContributionDependencies: checkDefinitionData.uiContributionDependencies,
                    checkConfigId: check.checkConfiguration.id,
                    checkDefinitionRefId: check.definitionRefId,
                    checkTypeDisplayName: checkDefinitionData.name,
                    checkTimeout: check.checkConfiguration.timeout,
                    checkConfig: check.checkConfiguration,
                    checkDefinition: checkDefinitionData.checkDefinition
                });
            }
        });

        return new ArrayItemProvider<ICheckDetailsRow>(tableRows);
    }

    private _onRowClicked = (event: React.SyntheticEvent<HTMLElement>, row: ITableRow<ICheckDetailsRow>): void => {
        this._onEditClicked(row.data);
    }

    private _onEditClicked = (checkDetails: ICheckDetailsRow): void => {

        //Dont allow users to edit the check without permissions
        if (!this.props.hasManagePermission) {
            return;
        }
        this._showCheckConfigPanel.value = true;
        this._selectedCheckRowDetails = { ...checkDetails };
    }

    private _getCheckDefinitionDetails = (definitionRefId: string): PipelineChecksContracts.CheckDefinitionData | null => {
        let checkDefinitionDetails = null;

        for (let checkDefinition of this.props.checkDefinitions) {
            if (equals(checkDefinition.definitionRefId, definitionRefId, true)) {
                checkDefinitionDetails = checkDefinition;
                break;
            }
        }

        return checkDefinitionDetails;
    }

    private _getTimeoutDetails = (timeout: number) => {
        let timeoutString = "";
        let timeoutTooltip = "";

        if (!timeout) {
            return { timeoutString, timeoutTooltip };
        }

        const minutesInHour = 60;
        const minutesInDay = minutesInHour * 24;

        let timeoutValue: Number;

        if (timeout % minutesInDay === 0) {
            timeoutValue = timeout / minutesInDay;
            timeoutString = format(Resources.TimeoutInDaysFormat, timeoutValue.toString());
            timeoutTooltip = timeoutValue.valueOf() === 1 ? Resources.Day : format(Resources.Days, timeoutValue.toString());
        }
        else if (timeout % minutesInHour === 0) {
            timeoutValue = timeout / minutesInHour;
            timeoutString = format(Resources.TimeoutInHoursFormat, timeoutValue.toString());
            timeoutTooltip = timeoutValue.valueOf() === 1 ? Resources.Hour : format(Resources.Hours, timeoutValue.toString());
        }
        else {
            timeoutValue = timeout;
            timeoutString = format(Resources.TimeoutInMinutesFormat, timeoutValue.toString());
            timeoutTooltip = timeoutValue.valueOf() === 1 ? Resources.Minute : format(Resources.Minutes, timeoutValue.toString());
        }

        return { timeoutString, timeoutTooltip };
    }

    private _renderOrderCell = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<ICheckDetailsRow>,
        tableItem: ICheckDetailsRow,
        ariaRowIndex: number
    ): JSX.Element => {
        let hasOrder = tableItem.checkOrder !== undefined;
        return (
            <SimpleTableCell
                key={"check-details-col" + columnIndex}
                ariaRowIndex={ariaRowIndex}
                columnIndex={columnIndex}
                tableColumn={tableColumn}
            >
                {hasOrder ? tableItem.checkOrder : null}
            </SimpleTableCell>
        );
    }

    private _renderCheckTypeDisplayNameCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ICheckDetailsRow>, tableItem: ICheckDetailsRow, ariaRowIndex: number): JSX.Element => {
        return (
            <SimpleTableCell
                key={"check-details-col" + columnIndex}
                className={css(tableItem.checkConfig.isDisabled && this._isDisableChecksFeatureEnabled && "check-disabled")}
                ariaRowIndex={ariaRowIndex}
                columnIndex={columnIndex}
                tableColumn={tableColumn}>
                <Tooltip text={tableItem.checkTypeDisplayName}>
                    <div className="body-m primary-text checks-table-cell">
                        {tableItem.checkTypeDisplayName}
                    </div>
                </Tooltip>
            </SimpleTableCell>
        );
    }

    private _renderDisplayNameCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ICheckDetailsRow>, tableItem: ICheckDetailsRow, ariaRowIndex: number): JSX.Element => {
        let isCheckDisabled = this._isDisableChecksFeatureEnabled && tableItem.checkConfig.isDisabled;
        return (
            <SimpleTableCell
                key={"check-details-col" + columnIndex}
                className={css(isCheckDisabled && "check-disabled")}
                ariaRowIndex={ariaRowIndex}
                columnIndex={columnIndex}
                tableColumn={tableColumn}>
                {this._getWrappedComponentElement(ChecksTableCellRenderer.DisplayName, tableItem)}
                {isCheckDisabled && (
                    <div className="checks-definition-pill">
                        <Pill
                            className={css("disabled-postfix")}
                            size={PillSize.compact}
                            variant={PillVariant.outlined}>
                            {Resources.DisabledPostfix}
                        </Pill>
                    </div>
                )}
                {this._showCheckIssues && tableItem.checkConfig.issue && (
                    <div className="checks-definition-pill">
                        <Pill
                            className={css("flex-noshrink margin-left-4",
                                !isCheckDisabled && tableItem.checkConfig.issue.type === PipelineChecksContracts.CheckIssueType.Warning && "outlined-warning",
                                !isCheckDisabled && tableItem.checkConfig.issue.type === PipelineChecksContracts.CheckIssueType.Error && "outlined-error",
                                isCheckDisabled && "disabled-postfix")}
                            size={PillSize.compact}
                            variant={PillVariant.outlined}>
                            {tableItem.checkConfig.issue.message}
                        </Pill>
                    </div>
                )}
            </SimpleTableCell>
        );
    }

    private _renderTimeoutCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ICheckDetailsRow>, tableItem: ICheckDetailsRow, ariaRowIndex: number): JSX.Element => {
        const { timeoutString, timeoutTooltip } = this._getTimeoutDetails(tableItem.checkTimeout);
        return (
            <SimpleTableCell
                key={"check-details-col" + columnIndex}
                className={css(tableItem.checkConfig.isDisabled && this._isDisableChecksFeatureEnabled && "check-disabled")}
                ariaRowIndex={ariaRowIndex}
                columnIndex={columnIndex}
                tableColumn={tableColumn}>
                <Tooltip text={timeoutTooltip}>
                    <div className="body-m primary-text">
                        {timeoutString}
                    </div>
                </Tooltip>
            </SimpleTableCell>
        );
    }

    private _renderDetailsCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ICheckDetailsRow>, tableItem: ICheckDetailsRow, ariaRowIndex: number): JSX.Element => {
        return (
            <SimpleTableCell
                key={"check-details-col" + columnIndex}
                className={css(tableItem.checkConfig.isDisabled && this._isDisableChecksFeatureEnabled && "check-disabled")}
                ariaRowIndex={ariaRowIndex}
                columnIndex={columnIndex}
                tableColumn={tableColumn}>
                {this._getWrappedComponentElement(ChecksTableCellRenderer.Details, tableItem)}
            </SimpleTableCell>);
    }

    private _renderDeleteCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ICheckDetailsRow>, tableItem: ICheckDetailsRow, ariaRowIndex: number): JSX.Element => {
        return (
            <TableCell className="bolt-table-cell-side-action" ariaRowIndex={ariaRowIndex} columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <div className="flex-column">
                    <Button
                        className={this._deleteButtonTableCellClass}
                        subtle={true}
                        ariaLabel={Resources.Delete}
                        excludeTabStop={true}
                        iconProps={{
                            iconName: "Delete",
                            className: "fontSizeML"
                        }}
                        disabled={!this.props.hasManagePermission}
                        onClick={e => {
                            this._onDeleteCheckClicked(tableItem);
                            e.preventDefault();
                        }}
                    />
                </div>
            </TableCell>
        );
    }

    private _getWrappedComponentElement(checkCellToRender: ChecksTableCellRenderer, tableItem: ICheckDetailsRow): JSX.Element {
        return (
            <WrappedComponent
                wrappedType={tableItem.checkComponentContributionId}
                dependencies={tableItem.checkComponentContributionDependencies}
                wrappedProps={{
                    onUpdateCheck: this._onUpdateCheck,
                    onClosePanel: this.onClosePanel,
                    resource: this._checkResource,
                    componentData: this.props.checkDefinitions,
                    checkConfigObject: tableItem.checkConfig,
                    checkTableCellToRender: checkCellToRender
                }} />
        );
    }

    private _onUpdateCheck = (record: PipelineChecksContracts.CheckConfiguration, definitionRefId: string, checkType: string): Promise<void> => {
        record.id = this._selectedCheckRowDetails.checkConfigId;
        return this.props.onUpdateCheck(record, definitionRefId, checkType, false);
    }

    private _onToogleCheckClicked = (checkDetailsRow: ICheckDetailsRow): Promise<void> => {
        checkDetailsRow.checkConfig.isDisabled = !checkDetailsRow.checkConfig.isDisabled;

        this._publishDisableCheckTelemetryEvent(checkDetailsRow);
        return this.props.onUpdateCheck(checkDetailsRow.checkConfig, checkDetailsRow.checkDefinitionRefId, checkDetailsRow.checkTypeDisplayName, true);
    }

    private _onChangeApprovalOrder = (checkDetailsRow: ICheckDetailsRow, newDefTypeId: string) => {
        const approvalConfiguration = checkDetailsRow.checkConfig as PipelineChecksContracts.ApprovalCheckConfiguration;
        const newConfiguration =  { ... approvalConfiguration, settings: {... approvalConfiguration.settings, definitionRef : { id: newDefTypeId } }};
        this.props.onUpdateCheck(newConfiguration, newDefTypeId, checkDetailsRow.checkTypeDisplayName, true);
    }

    private _publishDisableCheckTelemetryEvent = (checkDetailsRow: ICheckDetailsRow) => {
        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");

        telemetryService.publishEvent(
            "Checks",
            "DisableCheck",
            {
                IsCheckDisabled: checkDetailsRow.checkConfig.isDisabled,
                CheckName: checkDetailsRow.checkTypeDisplayName
            }
        );
    }

    public onClosePanel = (): void => {
        this._showCheckConfigPanel.value = false;
    }

    private _onDeleteCheckClicked = (checkDetailsRow: ICheckDetailsRow): void => {
        this._showDeleteCheckDialog.value = true;
        this._selectedCheckRowDetails = { ...checkDetailsRow };
        this._currentElement = document.activeElement as HTMLElement;
        if (this._currentElement) {
            this._currentElement.classList.remove(this._deleteButtonTableCellClass);
        }
    }

    private _onCancelDelete = (): void => {
        this._showDeleteCheckDialog.value = false;
        if (this._currentElement) {
            this._currentElement.focus();
            this._currentElement.classList.add(this._deleteButtonTableCellClass);
        }
    }

    private _onDeleteConfirm = (): void => {
        this._showDeleteCheckDialog.value = false;
        this.props.onDeleteCheck(this._selectedCheckRowDetails.checkConfigId, this._selectedCheckRowDetails.checkTypeDisplayName);
    }

    private _initTableColumns = () => {
        this._tableColumns = [
            {
                id: "checkDisplayName",
                name: Resources.CheckDisplayNameColumn,
                ariaLabel: Resources.CheckDisplayNameColumn,
                width: -45,
                readonly: true,
                className: "body-s primary-text checks-table-cell",
                renderCell: this._renderDisplayNameCell
            },
            {
                id: "checkType",
                name: Resources.CheckTypeColumnName,
                ariaLabel: Resources.CheckTypeColumnName,
                width: -25,
                readonly: true,
                className: "body-s primary-text",
                renderCell: this._renderCheckTypeDisplayNameCell
            },
            {
                id: "checkTimeout",
                name: Resources.CheckTimeoutColumn,
                ariaLabel: Resources.CheckTimeoutColumn,
                width: -15,
                readonly: true,
                className: "body-s primary-text",
                renderCell: this._renderTimeoutCell
            },
            {
                id: "checkDetails",
                name: Resources.CheckDetailsColumn,
                ariaLabel: Resources.CheckDetails,
                width: -15,
                readonly: true,
                className: "body-s primary-text",
                renderCell: this._renderDetailsCell
            }
        ];

        if (this._isEnableOrderingChecksEnabled) {
            const checkEvaluationOrderColumn = {
                id: "checkOrder",
                name: Resources.CheckOrderColumn,
                ariaLabel: Resources.CheckOrderColumn,
                width: -15,
                readonly: true,
                className: "body-s primary-text checks-table-cell",
                renderCell: this._renderOrderCell,
            };

            this._tableColumns = [checkEvaluationOrderColumn, ...this._tableColumns];
        }

        if (this._isDisableChecksFeatureEnabled) {
            const columnMore = new ColumnMore(this._getMenuForItem, this._showMenuForItem, undefined, 40);
            columnMore.widthStyle = IMeasurementStyle.Pixel;
            this._tableColumns.push(columnMore);
        } else {
            this._tableColumns.push({
                id: "delete",
                width: new ObservableValue(40),
                renderCell: this._renderDeleteCell,
                ariaLabel:
                    Resources.DeleteIconColumn
            });
        }
    }

    private _getMenuForItem = (checkDetailRow: ICheckDetailsRow): IMenuProps => {
        const menuItems: IMenuItem[] = [];
        if (this._isAllowChangeOrderEnabled && checkDetailRow.checkConfig.type.id === TaskCheckConstants.ApprovalCheckTypeId ) {
            const checkList = this.props.checkConfigurationData;
            const preApprovalChecksCount = checkList.filter(check => check.definitionRefId === TaskCheckConstants.PreApprovalDefTypeId).length;
            const mainApprovalChecksCount = checkList.filter(check => check.definitionRefId === TaskCheckConstants.MainApprovalDefTypeId || 
                check.definitionRefId === TaskCheckConstants.ApprovalCheckTypeId).length;
            const postApprovalChecksCount = checkList.filter(check => check.definitionRefId === TaskCheckConstants.PostApprovalDefTypeId).length;        

            const subItems: IMenuItem[] = [];
            if (checkDetailRow.checkDefinitionRefId !== TaskCheckConstants.PreApprovalDefTypeId && preApprovalChecksCount === 0) {
                subItems.push({
                    id: "to-precheck-approval",
                    onActivate: (item: IMenuItem, event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                        this._onChangeApprovalOrder(checkDetailRow, TaskCheckConstants.PreApprovalDefTypeId);
                        event.preventDefault();
                    },
                    text: Resources.ToPrecheckApproval
                });
            }

            if (checkDetailRow.checkDefinitionRefId !== TaskCheckConstants.MainApprovalDefTypeId && 
                checkDetailRow.checkDefinitionRefId !== TaskCheckConstants.ApprovalCheckTypeId &&
                mainApprovalChecksCount === 0) {
                subItems.push({
                    id: "to-approval",
                    onActivate: (item: IMenuItem, event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                        this._onChangeApprovalOrder(checkDetailRow, TaskCheckConstants.MainApprovalDefTypeId);
                        event.preventDefault();
                    },
                    text: Resources.ToApproval
                });
            }

            if (checkDetailRow.checkDefinitionRefId !== TaskCheckConstants.PostApprovalDefTypeId && postApprovalChecksCount === 0) {
                subItems.push({
                    id: "to-postcheck-approval",
                    onActivate: (item: IMenuItem, event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                        this._onChangeApprovalOrder(checkDetailRow, TaskCheckConstants.PostApprovalDefTypeId);
                        event.preventDefault();
                    },
                    text: Resources.ToPostcheckApproval
                });
            }

            if (subItems.length > 0) {
                // show menu only if we have options
                menuItems.push({
                    text: Resources.ChangeOrder,
                    id: "change-order",
                    subMenuProps: {
                        id: "change-order-submenu",
                        items: subItems
                    }
            });
        }

        }

        menuItems.push({
            text: Resources.Edit,
            id: "edit-check",
            onActivate: (menuItem: IMenuItem, event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                this._onEditClicked(checkDetailRow);
                event.preventDefault();
            }
        });

        let isCheckDisabled = this._isDisableChecksFeatureEnabled && checkDetailRow.checkConfig.isDisabled;
        let isCheckIssueError = this._showCheckErrors && !!checkDetailRow.checkConfig.issue;
        menuItems.push({
            text: checkDetailRow.checkConfig.isDisabled ? Resources.Enable : Resources.Disable,
            id: "disable-check",
            onActivate: (menuItem: IMenuItem, event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                this._onToogleCheckClicked(checkDetailRow);
                event.preventDefault();
            },
            disabled: isCheckDisabled && isCheckIssueError
        });

        menuItems.push({
            text: Resources.Delete,
            id: "delete-check",
            onActivate: (item: IMenuItem, event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                this._onDeleteCheckClicked(checkDetailRow);
                event.preventDefault();
            }
        });

        return {
            id: "stage-actions-menu",
            items: menuItems
        };
    }

    private _showMenuForItem = (checkDetailRow: ICheckDetailsRow) => {
        return this.props.hasManagePermission;
    }

    private _tableBreakpoints: ITableBreakpoint[] = [
        {
            breakpoint: ScreenBreakpoints.xsmall,
            columnWidths: [-40, -60, 0, 0, 40]
        },
        {
            breakpoint: ScreenBreakpoints.small,
            columnWidths: [-40, -60, 0, 0, 40]
        },
        {
            breakpoint: 800,
            columnWidths: [-35, -40, -25, 0, 40]
        },
        {
            breakpoint: ScreenBreakpoints.medium,
            columnWidths: [-20, -30, -25, -25, 40]
        }
    ];

    private _showDeleteCheckDialog: ObservableValue<boolean>;
    private _showCheckConfigPanel: ObservableValue<boolean>;
    private _selectedCheckRowDetails: ICheckDetailsRow;
    private _checkResource: PipelineChecksContracts.Resource;
    private _tableColumns: ITableColumn<ICheckDetailsRow>[];
    private _showCheckErrors: boolean;
    private _showCheckIssues: boolean;
    private _isDisableChecksFeatureEnabled: boolean;
    private _isAllowChangeOrderEnabled: boolean;
    private _isEnableOrderingChecksEnabled: boolean;
    private _currentElement: HTMLElement | null;
    private readonly _deleteButtonTableCellClass: string = "bolt-table-cell-content-reveal";
}